import { ReactNode } from 'react'
import { defaultUrlTransform } from 'react-markdown'
import { KnowledgeAskCitation, KnowledgeDocumentListItem } from '@digital-twins/shared-types'

import { CreativeMedia } from '@/components/chat/CreativeMedia'
import { ThreadAssetView } from '@/lib/api'
import { documentTitle } from '@/pages/KnowledgeHubPage/libraryHelpers'

import { ThreadDocChip } from './ThreadDocChip'

const CREATIVE_REF = /^(asset|doc):[A-Za-z0-9_-]{1,64}$/

export interface CreativeMarkdownImageProps {
  src?: string
  alt?: string
}

export interface CreativeMarkdownLinkProps {
  href?: string
  children?: ReactNode
}

export interface CreativeMarkdownHandlers {
  urlTransform: (url: string) => string
  renderImage: (props: CreativeMarkdownImageProps) => ReactNode
  renderLink: (props: CreativeMarkdownLinkProps) => ReactNode
}

interface CreateCreativeMarkdownHandlersArgs {
  assets?: Array<ThreadAssetView>
  docsByToken?: Map<string, KnowledgeDocumentListItem>
  onOpenCitation?: (index: number, citation: KnowledgeAskCitation) => void
}

/** Extends react-markdown's default so `asset:` / `doc:` refs survive. */
export function creativeUrlTransform(url: string) {
  return CREATIVE_REF.test(url) ? url : defaultUrlTransform(url)
}

function parseCreativeRef(
  url: string | undefined,
): { scheme: 'asset' | 'doc'; token: string } | null {
  if (!url || !CREATIVE_REF.test(url)) {
    return null
  }
  const separator = url.indexOf(':')
  const scheme = url.slice(0, separator)
  const token = url.slice(separator + 1)
  if (scheme !== 'asset' && scheme !== 'doc') {
    return null
  }
  return { scheme, token }
}

function nodeText(children: ReactNode): string {
  if (children == null || typeof children === 'boolean') {
    return ''
  }
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children)
  }
  if (Array.isArray(children)) {
    return children.map(nodeText).join('')
  }
  return ''
}

function citationFromDocument(
  doc: KnowledgeDocumentListItem,
  label: string,
): KnowledgeAskCitation {
  return {
    documentName: doc.originalFilename || label || doc.token,
    documentToken: doc.token,
    snippet: '',
    granularity: 'document',
    extractionVersion: 'v0',
  }
}

/**
 * Resolves `asset:` / `doc:` markdown refs against the thread's asset list
 * and document index. Used by both chat preview and saved artifact cards.
 */
export function createCreativeMarkdownHandlers(
  args: CreateCreativeMarkdownHandlersArgs,
): CreativeMarkdownHandlers {
  const { assets = [], docsByToken, onOpenCitation } = args

  const assetsByToken = new Map(
    assets.map((asset) => [asset.token, asset]),
  )

  function renderImage(props: CreativeMarkdownImageProps): ReactNode {
    const { src, alt } = props
    const ref = parseCreativeRef(src)
    if (!ref) {
      return undefined
    }
    if (ref.scheme !== 'asset') {
      return alt || null
    }
    const asset = assetsByToken.get(ref.token)
    const publicUrl = asset?.ref.publicUrl
    if (!publicUrl) {
      return alt || null
    }
    return (
      <CreativeMedia
        src={publicUrl}
        mimeType={asset.ref.mimeType}
        alt={alt}
        className="max-w-full h-auto"
        dataTestId={`creative-markdown-media-${ref.token}`}
      />
    )
  }

  function renderLink(props: CreativeMarkdownLinkProps): ReactNode {
    const { href, children } = props
    const ref = parseCreativeRef(href)
    if (!ref) {
      return undefined
    }
    if (ref.scheme !== 'doc') {
      return children
    }
    const doc = docsByToken?.get(ref.token)
    if (!doc) {
      return children
    }
    const title = documentTitle(doc) || nodeText(children) || doc.token
    return (
      <ThreadDocChip
        title={title}
        documentToken={doc.token}
        onOpen={
          onOpenCitation
            ? () => {
                onOpenCitation(1, citationFromDocument(doc, title))
              }
            : undefined
        }
      />
    )
  }

  return {
    urlTransform: creativeUrlTransform,
    renderImage,
    renderLink,
  }
}
